import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import NumberFormat from 'react-number-format'
import adminService from '../../services/adminService'
import PageTitle from '../../components/Common/PageTitle'
import Skeleton from 'react-loading-skeleton'
import { toast } from 'react-toastify'

const AdminSubscriptionManagement = () => {
  const navigate = useNavigate()
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({})
  const [filters, setFilters] = useState({
    status: '',
    planId: '',
    page: 1
  })
  const [subscriptionPlans, setSubscriptionPlans] = useState({})
  const [revenueStats, setRevenueStats] = useState(null)
  const [showPlanEditor, setShowPlanEditor] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [paymentsResponse, plans, dashboardStats] = await Promise.all([
        adminService.getPayments({
          page: filters.page,
          limit: 20,
          status: filters.status,
          planId: filters.planId
        }),
        adminService.getSubscriptionPlans(),
        adminService.getDashboardStats()
      ])
      
      setPayments(paymentsResponse.payments)
      setPagination(paymentsResponse)
      setSubscriptionPlans(plans)
      setRevenueStats({
        totalRevenue: dashboardStats.totalRevenue,
        monthlyRevenue: dashboardStats.monthlyRevenue,
        subscriptionBreakdown: dashboardStats.subscriptionBreakdown
      })
    } catch (error) {
      console.error('Error loading subscription data:', error)
      toast.error('Failed to load subscription data')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    // Check if user is admin
    if (!adminService.isAdmin()) {
      toast.error('Access denied. Admin privileges required.')
      navigate('/dashboards')
      return
    }

    loadData()
  }, [navigate, loadData])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when other filters change
    }))
  }

  const handleExportPayments = async () => {
    try {
      const allPayments = await adminService.getPayments({
        page: 1,
        limit: 1000 // Get more records for export
      })
      
      // Create CSV content
      const headers = ['Date', 'User Name', 'User Email', 'Plan', 'Billing Cycle', 'Amount', 'Currency', 'Status', 'Payment Method']
      const csvContent = [
        headers.join(','),
        ...allPayments.payments.map(payment => [
          new Date(payment.created_at).toLocaleDateString(),
          payment.users?.name || 'N/A',
          payment.users?.email || 'N/A',
          payment.plan_id,
          payment.billing_cycle,
          payment.amount,
          payment.currency,
          payment.status,
          payment.payment_method || 'UPI'
        ].join(','))
      ].join('\n')

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payments-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success('Payments data exported successfully')
    } catch (error) {
      console.error('Error exporting payments:', error)
      toast.error('Failed to export payments data')
    }
  }

  const PlanCard = ({ planKey, plan }) => {
    const userCount = revenueStats?.subscriptionBreakdown?.[planKey] || 0
    const isPopular = plan.popular

    return (
      <div className={`bg-white rounded-lg border-2 p-6 relative ${
        isPopular ? 'border-blue-500 shadow-lg' : 'border-gray-200 shadow-sm'
      }`}>
        {isPopular && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              Most Popular
            </span>
          </div>
        )}
        
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
          <div className="mt-2">
            <span className="text-3xl font-bold text-gray-900">
              {plan.billing?.monthly?.price ? (
                <NumberFormat
                  value={plan.billing.monthly.price}
                  displayType="text"
                  thousandSeparator={true}
                  prefix="₹"
                  renderText={(value) => value}
                />
              ) : (
                'Free'
              )}
            </span>
            {plan.billing?.monthly && (
              <span className="text-gray-600">/month</span>
            )}
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Active Users</span>
            <span className="font-semibold text-gray-900">{userCount}</span>
          </div>
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Features</h4>
            <ul className="space-y-1">
              {plan.features.slice(0, 5).map((feature, index) => (
                <li key={index} className="text-xs text-gray-600 flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="text-xs text-gray-500 space-y-1">
            <div>Invoices: {plan.limitations?.invoicesSaveExport === -1 ? 'Unlimited' : plan.limitations?.invoicesSaveExport}</div>
            <div>Clients: {plan.limitations?.clients === -1 ? 'Unlimited' : plan.limitations?.clients}</div>
            <div>Products: {plan.limitations?.products === -1 ? 'Unlimited' : plan.limitations?.products}</div>
          </div>
        </div>

        <button
          onClick={() => {
            setEditingPlan({ key: planKey, ...plan })
            setShowPlanEditor(true)
          }}
          className="w-full mt-4 bg-gray-100 text-gray-700 py-2 rounded-md hover:bg-gray-200 text-sm font-medium"
        >
          Edit Plan
        </button>
      </div>
    )
  }

  const PlanEditorModal = ({ plan, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      name: plan?.name || '',
      popular: plan?.popular || false,
      monthlyPrice: plan?.billing?.monthly?.price || 0,
      features: plan?.features?.join('\n') || '',
      invoiceLimit: plan?.limitations?.invoicesSaveExport || 10,
      clientLimit: plan?.limitations?.clients || 5,
      productLimit: plan?.limitations?.products || 5
    })

    const handleSubmit = (e) => {
      e.preventDefault()
      // This would typically update the plan in the database
      // For now, we'll just show a message
      toast.info('Plan editing is not yet implemented in the database. This would update the subscription plan configuration.')
      onClose()
    }

    if (!plan) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Edit Plan: {plan.name}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Price (₹)
                </label>
                <input
                  type="number"
                  value={formData.monthlyPrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthlyPrice: parseFloat(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Limit
                </label>
                <input
                  type="number"
                  value={formData.invoiceLimit}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoiceLimit: parseInt(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  min="-1"
                  placeholder="-1 for unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Limit
                </label>
                <input
                  type="number"
                  value={formData.clientLimit}
                  onChange={(e) => setFormData(prev => ({ ...prev, clientLimit: parseInt(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  min="-1"
                  placeholder="-1 for unlimited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Limit
                </label>
                <input
                  type="number"
                  value={formData.productLimit}
                  onChange={(e) => setFormData(prev => ({ ...prev, productLimit: parseInt(e.target.value) }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  min="-1"
                  placeholder="-1 for unlimited"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Features (one per line)
              </label>
              <textarea
                rows={6}
                value={formData.features}
                onChange={(e) => setFormData(prev => ({ ...prev, features: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter features, one per line"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="popular"
                checked={formData.popular}
                onChange={(e) => setFormData(prev => ({ ...prev, popular: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="popular" className="ml-2 block text-sm text-gray-900">
                Mark as Popular Plan
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  if (loading && payments.length === 0) {
    return (
      <div className="p-4">
        <PageTitle title="Subscription Management" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm border">
              <Skeleton height={20} className="mb-2" />
              <Skeleton height={32} className="mb-4" />
              <Skeleton count={5} height={16} className="mb-1" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <PageTitle title="Subscription Management" />
        <div className="flex space-x-2">
          <button
            onClick={handleExportPayments}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
          >
            Export Payments
          </button>
          <button
            onClick={loadData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Revenue Statistics */}
      {revenueStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Revenue</h3>
            <p className="text-3xl font-bold text-green-600">
              <NumberFormat
                value={revenueStats.totalRevenue}
                displayType="text"
                thousandSeparator={true}
                prefix="₹"
                renderText={(value) => value}
              />
            </p>
            <p className="text-sm text-gray-500">All time earnings</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Revenue</h3>
            <p className="text-3xl font-bold text-blue-600">
              <NumberFormat
                value={revenueStats.monthlyRevenue}
                displayType="text"
                thousandSeparator={true}
                prefix="₹"
                renderText={(value) => value}
              />
            </p>
            <p className="text-sm text-gray-500">This month</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Subscriptions</h3>
            <p className="text-3xl font-bold text-purple-600">
              {Object.values(revenueStats.subscriptionBreakdown).reduce((sum, count) => sum + count, 0) - (revenueStats.subscriptionBreakdown.free || 0)}
            </p>
            <p className="text-sm text-gray-500">Paid subscribers</p>
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(subscriptionPlans).map(([planKey, plan]) => (
            <PlanCard key={planKey} planKey={planKey} plan={plan} />
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={filters.planId}
              onChange={(e) => handleFilterChange('planId', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Plans</option>
              {Object.keys(subscriptionPlans).filter(key => key !== 'free').map(planKey => (
                <option key={planKey} value={planKey}>
                  {subscriptionPlans[planKey].name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: '', planId: '', page: 1 })}
              className="w-full p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payment.users?.name || 'Unknown User'}</div>
                    <div className="text-sm text-gray-500">{payment.users?.email || 'No email'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      payment.plan_id === 'pro'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {payment.plan_id}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">{payment.billing_cycle}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <NumberFormat
                      value={payment.amount}
                      displayType="text"
                      thousandSeparator={true}
                      prefix="₹"
                      renderText={(value) => value}
                    />
                    <div className="text-xs text-gray-500">{payment.currency}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      payment.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : payment.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{new Date(payment.created_at).toLocaleDateString()}</div>
                    {payment.completed_at && (
                      <div className="text-xs text-gray-400">
                        Completed: {new Date(payment.completed_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {payment.payment_method || 'UPI'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                disabled={!pagination.hasPrevPage}
                onClick={() => handleFilterChange('page', filters.page - 1)}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={!pagination.hasNextPage}
                onClick={() => handleFilterChange('page', filters.page + 1)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((filters.page - 1) * 20) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(filters.page * 20, pagination.totalCount)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    disabled={!pagination.hasPrevPage}
                    onClick={() => handleFilterChange('page', filters.page - 1)}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    ‹
                  </button>
                  
                  {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                    const page = filters.page <= 3 ? i + 1 : filters.page - 2 + i
                    return (
                      <button
                        key={page}
                        onClick={() => handleFilterChange('page', page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === filters.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  })}

                  <button
                    disabled={!pagination.hasNextPage}
                    onClick={() => handleFilterChange('page', filters.page + 1)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    ›
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plan Editor Modal */}
      {showPlanEditor && editingPlan && (
        <PlanEditorModal
          plan={editingPlan}
          onClose={() => {
            setShowPlanEditor(false)
            setEditingPlan(null)
          }}
          onSave={() => {
            // Handle plan save
            setShowPlanEditor(false)
            setEditingPlan(null)
          }}
        />
      )}
    </div>
  )
}

export default AdminSubscriptionManagement